import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Editor } from '@tinymce/tinymce-react';
import { baseURL, baseURLnoapi } from '../../Constants';
import { Article } from '../../context/NewsContext';
import { AuthContext } from '../../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage } from '@fortawesome/free-solid-svg-icons';
import { analytics } from '../../services/analytics';
import '../../styles/admin/admin-article-editor.scss';

interface FormData {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  imageUrl: string;
  category: string;
  tags: string[];
}

const AdminArticleEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const editorRef = useRef<any>(null);
  
  // Get auth context
  const { token, isAdmin } = useContext(AuthContext);
  
  const [formData, setFormData] = useState<FormData>({
    id: '',
    title: '',
    excerpt: '',
    content: '',
    imageUrl: '',
    category: '',
    tags: []
  });
  
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Available categories
  const categories = [
    'Announcement',
    'Results',
    'Player News', 
    'Upcoming Events',
    'Previous Events',
    'NCS News',
    'Information',
    'Miscellaneous'
  ];

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    return {
      'Authorization': `Bearer ${token}`
    };
  };

  // Redirect if not an admin
  useEffect(() => {
    if (!isAdmin) {
      navigate('/admin/login');
    }
  }, [isAdmin, navigate]);

  useEffect(() => {
    if (isEditMode) {
      fetchArticle();
    } else {
      // Generate a new ID for a new article
      setFormData(prev => ({
        ...prev,
        id: crypto.randomUUID()
      }));
    }
    
    // Set page title
    document.title = isEditMode 
      ? 'Edit Article - Admin Dashboard' 
      : 'Create Article - Admin Dashboard';
  }, [id]);

  const fetchArticle = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${baseURL}/NewsArticle/${id}`, {
        headers: token ? getAuthHeaders() : {}
      });
      const article: Article = response.data;
      
      setFormData({
        id: article.id,
        title: article.title,
        excerpt: article.excerpt,
        content: article.content,
        imageUrl: article.imageUrl || '',
        category: article.category,
        tags: article.tags || []
      });
    } catch (err) {
      console.error('Error fetching article:', err);
      setError('Failed to load article data.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() === '') return;
    
    // Check if tag already exists
    if (!formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
    }
    
    setTagInput('');
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!token) {
      setImageError('You need to be logged in as an admin to upload images.');
      return;
    }
    
    const file = e.target.files?.[0];
    if (!file) return;
  
    // Basic validation
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setImageError('Invalid file type. Only JPG, PNG, and GIF files are allowed.');
      return;
    }
  
    setImageUploading(true);
    setImageError(null);
  
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', file);
      formDataObj.append('type', 'header'); // Specify this is a header image
  
      // Make API call to upload the image WITH AUTH HEADERS
      const response = await axios.post(
        `${baseURL}/NewsArticle/${formData.id}/image`,
        formDataObj,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      // Update the image URL in the form
      if (response.data && response.data.imageUrl) {
        setFormData(prev => ({
          ...prev,
          imageUrl: response.data.imageUrl
        }));
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      setImageError('currently not working, please add image after creating the article');
    } finally {
      setImageUploading(false);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handlePreview = () => {
    // Get content directly from the editor for preview
    if (editorRef.current) {
      const currentContent = editorRef.current.getContent();
      setPreviewContent(currentContent);
    }
    setShowPreview(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Get the latest content from the editor only when needed (on submit)
    let contentToSave = formData.content;
    if (editorRef.current) {
      contentToSave = editorRef.current.getContent();
    }
    
    setSaving(true);
    setError(null);
    
    try {
      const articleData = {
        ...formData,
        content: contentToSave, // Use the content we just extracted
        date: new Date().toISOString()
      };
      
      let response;
      
      if (isEditMode) {
        response = await axios.put(
          `${baseURL}/NewsArticle/${id}`,
          articleData,
          {
            headers: token ? getAuthHeaders() : {}
          }
        );
        // Track article edit
        analytics.admin.articleEdited(formData.id, formData.category);
      } else {
        response = await axios.post(
          `${baseURL}/NewsArticle`,
          articleData,
          {
            headers: token ? getAuthHeaders() : {}
          }
        );
        // Track article creation
        analytics.admin.articleCreated(formData.category, !!formData.imageUrl);
      }

      // Redirect to articles management page
      navigate('/admin/articles');
    } catch (err) {
      console.error('Error saving article:', err);
      setError('Failed to save article. Please try again.');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-article-page article-loading">
        <div className="article-container">
          <div className="article-loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-article-page">
      <div className="article-container">
        <div className="article-header">
          <h1>{isEditMode ? 'Edit Article' : 'Create New Article'}</h1>
        </div>
        
        {error && (
          <div className="article-alert article-alert-danger">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="article-form-grid">
            <div className="article-form-main">
              <div className="article-form-group">
                <label htmlFor="title">Title</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter article title"
                />
              </div>
              
              <div className="article-form-group">
                <label htmlFor="excerpt">Excerpt</label>
                <textarea
                  id="excerpt"
                  name="excerpt"
                  value={formData.excerpt}
                  onChange={handleInputChange}
                  rows={3}
                  required
                  placeholder="Enter a short summary of the article"
                ></textarea>
              </div>
              
              <div className="article-form-group">
                <label htmlFor="content">Content</label>
                <div className="article-editor-container">
                <Editor
                  apiKey="eln0pwahx72myxwccckxh49g4gg5tlkaxoode5npu64nnsfm"
                  onInit={(evt, editor) => {
                    editorRef.current = editor;
                  }}
                  initialValue={formData.content}
                  init={{
                    height: 500,
                    menubar: true,
                    skin: 'oxide-dark',
                    content_css: 'dark',
                    
                    // Enhanced plugins - adding imagetools for better image editing
                    plugins: [
                      'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                      'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                      'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount', 'imagetools'
                    ],
                    
                    toolbar: 'undo redo | blocks | ' +
                      'bold italic forecolor | alignleft aligncenter ' +
                      'alignright alignjustify | bullist numlist outdent indent | ' +
                      'image media | removeformat | help',
                    
                    // Enhanced content styles with image classes
                    content_style: `
                      body { font-family:Roboto,Arial,sans-serif; font-size:16px; color:#ffffff; background-color: #1c2938; }
                      
                      /* Image styling options */
                      .article-img-fluid { max-width: 100%; height: auto; border-radius: 4px; margin-bottom: 1rem; }
                      .article-img-left { float: left; margin-right: 1rem; margin-bottom: 0.5rem; max-width: 50%; }
                      .article-img-right { float: right; margin-left: 1rem; margin-bottom: 0.5rem; max-width: 50%; }
                      .article-img-center { display: block; margin-left: auto; margin-right: auto; margin-bottom: 1rem; }
                      
                      /* Figure and caption styling */
                      figure.image { display: inline-block; border: 1px solid #2a3a50; border-radius: 4px; padding: 4px; margin: 0 2px; background: #111922; }
                      figure.image img { border-radius: 2px; }
                      figure.image figcaption { margin: 6px 0 0; padding: 6px; background: #0a1018; text-align: center; font-size: 0.8rem; color: #909eba; }
                    `,
                    
                    // Image configuration
                    image_caption: true,
                    image_advtab: true,
                    image_dimensions: true,
                    image_class_list: [
                      { title: 'Responsive', value: 'article-img-fluid' },
                      { title: 'Left aligned', value: 'article-img-left' },
                      { title: 'Right aligned', value: 'article-img-right' },
                      { title: 'Center aligned', value: 'article-img-center' }
                    ],
                    
                    paste_data_images: true,
                    branding: false,
                    file_picker_types: 'image',
                    
                    // Using iframe mode can help with cursor issues
                    inline: false,
                    iframe: true,
                    
                    // Updated image upload handler to specify content images
                    images_upload_handler: async function (
                      blobInfo: { 
                        blob: () => Blob; 
                        filename: () => string 
                      }, 
                      progress: (percent: number) => void
                    ) {
                      if (!token) {
                        throw new Error('Authentication required. Please log in as admin.');
                      }
                      const formDataObj = new FormData();
                      formDataObj.append('file', blobInfo.blob(), blobInfo.filename());
                      formDataObj.append('type', 'content'); // Specify this is a content image
                      
                      try {
                        // Make API call to upload the image WITH AUTH HEADERS
                        const response = await axios.post(
                          `${baseURL}/NewsArticle/${formData.id}/image`,
                          formDataObj,
                          {
                            headers: {
                              'Content-Type': 'multipart/form-data',
                              ...(token ? getAuthHeaders() : {})
                            },
                            onUploadProgress: (progressEvent) => {
                              if (progressEvent.total) {
                                const percentCompleted = Math.round(
                                  (progressEvent.loaded * 100) / progressEvent.total
                                );
                                progress(percentCompleted);
                              }
                            }
                          }
                        );
                        
                        // Return the URL for TinyMCE to use
                        if (response.data && response.data.imageUrl) {
                          // Return the full URL instead of relative path
                          return `${baseURLnoapi}${response.data.imageUrl}`;
                        }else {
                          throw new Error('Image URL not found in response');
                        }
                      } catch (err) {
                        console.error('Error uploading image:', err);
                        throw new Error('Image upload failed');
                      }
                    },
                    automatic_uploads: true
                  }}
                />
                </div>
              </div>
            </div>
            
            <div className="article-form-sidebar">
              <div className="article-sidebar-section">
                <h3>Article Settings</h3>
                
                <div className="article-form-group">
                  <label htmlFor="category">Category</label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a category</option>
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="article-form-group">
                  <label htmlFor="tags">Tags</label>
                  <div className="article-tag-input-container">
                    <input
                      type="text"
                      id="tagInput"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      placeholder="Add a tag..."
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddTag();
                        }
                      }}
                    />
                    <button 
                      type="button" 
                      onClick={handleAddTag}
                      className="article-btn article-btn-sm"
                    >
                      Add
                    </button>
                  </div>
                  
                  <div className="article-tags-list">
                    {formData.tags.map(tag => (
                      <div key={tag} className="article-tag-item">
                        <span>{tag}</span>
                        <button 
                          type="button" 
                          onClick={() => handleRemoveTag(tag)}
                          className="article-remove-tag"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    {formData.tags.length === 0 && (
                      <div className="article-no-tags">No tags added</div>
                    )}
                  </div>
                </div>
                
                <div className="article-form-group">
                  <label htmlFor="article-featured-image">Featured Image</label>
                  {formData.imageUrl && (
                    <div className="article-image-preview">
                      <img
                        src={formData.imageUrl.startsWith('http')
                          ? formData.imageUrl
                          : `${baseURLnoapi}${formData.imageUrl}`}
                        alt="Article featured"
                      />
                    </div>
                  )}

                  <div className="article-image-upload-controls">
                    <input
                      id="article-featured-image"
                      type="file"
                      ref={fileInputRef}
                      accept="image/jpeg,image/png,image/gif"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                    />
                    <button
                      type="button"
                      className="article-btn-outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={imageUploading}
                    >
                      {formData.imageUrl ? (
                        <>Change Image</>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faImage} /> Upload Image
                        </>
                      )}
                    </button>
                    
                    {imageUploading && <span className="article-loading-spinner small"></span>}
                    
                    {imageError && (
                      <div className="article-image-error">
                        {imageError}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="article-sidebar-section actions">
                <div className="article-action-buttons">
                  <button 
                    type="button" 
                    className="article-btn-secondary"
                    onClick={() => navigate('/admin/articles')}
                  >
                    CANCEL
                  </button>
                  <button 
                    type="button" 
                    className="article-btn-outline-primary"
                    onClick={handlePreview}
                  >
                    PREVIEW
                  </button>
                  <button 
                    type="submit" 
                    className="article-btn-primary"
                    disabled={saving}
                  >
                    {saving ? 'SAVING...' : 'PUBLISH ARTICLE'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
        
        {/* Article Preview Modal */}
        {showPreview && (
          <div className="article-preview-modal-overlay" onClick={() => setShowPreview(false)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowPreview(false); }}>
            <div className="article-preview-modal" onClick={e => e.stopPropagation()} role="dialog" onKeyDown={(e) => e.stopPropagation()}>
              <div className="article-preview-header">
                <h2>Article Preview</h2>
                <button 
                  type="button" 
                  className="article-close-preview"
                  onClick={() => setShowPreview(false)}
                >
                  ×
                </button>
              </div>
              <div className="article-preview-body">
                <div className="article-preview">
                  <div className="article-preview-header">
                    <h1 className="article-preview-title">{formData.title}</h1>
                    <div className="article-preview-meta">
                      <div className="article-date">
                        {new Date().toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </div>
                      <div className="article-category">
                        {formData.category}
                      </div>
                    </div>
                    {formData.tags.length > 0 && (
                      <div className="article-preview-tags">
                        {formData.tags.map(tag => (
                          <span key={tag} className="article-preview-tag">{tag}</span>
                        ))}
                      </div>
                    )}
                    {formData.imageUrl && (
                      <div className="article-preview-featured-image">
                        <img 
                          src={formData.imageUrl.startsWith('http') 
                            ? formData.imageUrl 
                            : `${baseURLnoapi}${formData.imageUrl}`} 
                          alt={formData.title} 
                        />
                      </div>
                    )}
                  </div>
                  <div className="article-preview-content" dangerouslySetInnerHTML={{ __html: previewContent }} />
                </div>
              </div>
              <div className="article-preview-footer">
                <button 
                  type="button" 
                  onClick={() => setShowPreview(false)}
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminArticleEditorPage;